import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import appcolors from "../../../constants/Colors";
import globalStyle from "../../../globalStyle";
import {
  getCompanyDetails,
  updateCompanyStatus,
} from "../../../services/comanyApiFn";
import { handleOpenSnackBar } from "../../../utils";
import AppLoader from "../../AppLoader";
import SubServicesCard from "../../Cards/SubServicesCard";
import PaperContainer from "../../Containers/PaperContainer";
import GlobalButton from "../../CustomButton";
import LicenseImageDialog from "../../Dialogs/LicenseImageDialog";
import IOSSwitch from "../../IsoSwitch";
import Label from "../../Label";
import LocationMap from "../../Map/LocationMap";
import NavigationDrawer from "../../NavigationDrawer";
import RomanianPhoneInput from "../../RomanianPhoneInput";
import TextInput from "../../TextInputs";
import DetailHeader from "../DetailHeader";

const CompanyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const licenseImageDialogRef = useRef(null);
  const currentLanguage = useSelector(
    (state) => state.languageReducer.currentLanguage
  );
  const [company, setCompany] = useState({
    data: null,
    isLoading: false,
    error: null,
  });

  const fetchCompanyDetails = async (companyId) => {
    try {
      setCompany((prev) => ({ ...prev, isLoading: true, error: null }));

      const response = await getCompanyDetails(companyId);

      if (response.data.status === "success") {
        setCompany((prev) => ({
          ...prev,
          data: response.data.data,
        }));
      } else {
        setCompany((prev) => ({
          ...prev,
          error: response.data.message,
        }));
      }
    } catch (error) {
      setCompany((prev) => ({
        ...prev,
        error: error.message,
      }));
    } finally {
      setCompany((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  useEffect(() => {
    if (id) {
      fetchCompanyDetails(id);
    }
  }, [id]);

  const companyData = company.data;

  const handleUpdateStatus = async (checked) => {
    try {
      const payload = {
        status: checked,
      };

      const response = await updateCompanyStatus(id, payload);
      if (response.data.status === "success") {
        fetchCompanyDetails(id);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    }
  };

  const handleUpdateVerified = async (checked) => {
    try {
      const payload = {
        isVerified: checked,
      };

      const response = await updateCompanyStatus(id, payload);
      if (response.data.status === "success") {
        fetchCompanyDetails(id);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    }
  };

  const handleUpdateAllowedByAdmin = async (checked) => {
    try {
      const payload = {
        isAllowedByAdmin: checked,
      };

      const response = await updateCompanyStatus(id, payload);
      if (response.data.status === "success") {
        fetchCompanyDetails(id);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    }
  };

  if (company.isLoading) {
    return (
      <NavigationDrawer>
        <AppLoader isVisible={company.isLoading} />
      </NavigationDrawer>
    );
  }

  if (company.error) {
    return (
      <NavigationDrawer>
        <PaperContainer
          title={t("companyDetails")}
          subtitle={t("viewAndManageCompanyDetails")}
          showBackButton={true}
          onBack={() => navigate(-1)}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={3}
            minHeight="400px"
            py={5}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              p={4}
              borderRadius={3}
              backgroundColor="#fff5f5"
              border="2px solid #fee"
              maxWidth="500px"
              width="100%"
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: "5rem",
                  color: "#d32f2f",
                  mb: 2,
                }}
              />
              <Typography
                variant="h5"
                fontWeight={600}
                color="error"
                textAlign="center"
                mb={1}
              >
                {t("errorOccurred") || "Error Occurred"}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
                sx={{
                  mb: 3,
                  wordBreak: "break-word",
                }}
              >
                {company.error}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ width: "100%", mt: 2 }}
              >
                <GlobalButton
                  variant="outlined"
                  btnText="goBack"
                  handleClickBtn={() => navigate(-1)}
                  styleProps={{
                    flex: 1,
                    height: 45,
                    borderRadius: "6px",
                    borderColor: appcolors.primary,
                    color: appcolors.primary,
                  }}
                />
                <GlobalButton
                  variant="contained"
                  btnText="retry"
                  handleClickBtn={() => {
                    if (id) {
                      fetchCompanyDetails(id);
                    }
                  }}
                  styleProps={{
                    flex: 1,
                    height: 45,
                    borderRadius: "6px",
                    backgroundColor: appcolors.primary,
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </PaperContainer>
      </NavigationDrawer>
    );
  }

  if (!companyData) {
    return (
      <NavigationDrawer>
        <PaperContainer
          title={t("companyDetails")}
          subtitle={t("viewAndManageCompanyDetails")}
          showBackButton={true}
          onBack={() => navigate(-1)}
        >
          <Typography variant="h6">{t("noDataFound")}</Typography>
        </PaperContainer>
      </NavigationDrawer>
    );
  }

  return (
    <NavigationDrawer>
      <AppLoader isVisible={company.isLoading} />
      <PaperContainer
        title={t("companyDetails")}
        subtitle={t("viewAndManageCompanyDetails")}
        showBackButton={true}
        onBack={() => navigate(-1)}
      >
        {/* Company Profile Section */}
        <DetailHeader
          headerData={{
            title: companyData?.name,
            image: companyData?.profileImg,
            licenseImage: companyData?.licenseImg,
          }}
          handleViewLicenseImage={() =>
            licenseImageDialogRef.current?.openDialog({
              imageUrl: companyData?.licenseImg,
            })
          }
        />

        {/* Control Section */}
        <Typography variant="h6" fontWeight={600} my={2}>
          {t("control")}
        </Typography>

        <Grid container spacing={3} mb={3}>
          {/* Status Card */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#ffffff",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={2} flex={1}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "50%",
                      backgroundColor: companyData.status
                        ? "rgba(0, 193, 0, 0.1)"
                        : "rgba(255, 0, 0, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {companyData.status ? (
                      <PowerSettingsNewIcon
                        sx={{
                          color: "#00c100",
                          fontSize: "1.5rem",
                        }}
                      />
                    ) : (
                      <CancelIcon
                        sx={{
                          color: "#ff0000",
                          fontSize: "1.5rem",
                        }}
                      />
                    )}
                  </Box>
                  <Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize="0.75rem"
                      textTransform="uppercase"
                      letterSpacing={0.5}
                    >
                      {t("status")}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color={companyData.status ? "#00c100" : "#ff0000"}
                    >
                      {companyData.status ? t("active") : t("inactive")}
                    </Typography>
                  </Stack>
                </Stack>
                <IOSSwitch
                  checked={companyData.status || false}
                  onChange={(e) => handleUpdateStatus(e.target.checked)}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Verified Card */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#ffffff",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={2} flex={1}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "50%",
                      backgroundColor: companyData.isVerified
                        ? "rgba(0, 193, 0, 0.1)"
                        : "rgba(255, 165, 0, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        color: companyData.isVerified ? "#00c100" : "#ffa500",
                        fontSize: "1.5rem",
                      }}
                    />
                  </Box>
                  <Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize="0.75rem"
                      textTransform="uppercase"
                      letterSpacing={0.5}
                    >
                      {t("verified")}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color={companyData.isVerified ? "#00c100" : "#ffa500"}
                    >
                      {companyData.isVerified
                        ? t("verified")
                        : t("notVerified")}
                    </Typography>
                  </Stack>
                </Stack>
                <IOSSwitch
                  checked={companyData.isVerified || false}
                  onChange={(e) => handleUpdateVerified(e.target.checked)}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Allowed By Admin Card */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#ffffff",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={2} flex={1}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "50%",
                      backgroundColor: companyData.isAllowedByAdmin
                        ? "rgba(121, 34, 216, 0.1)"
                        : "rgba(255, 0, 0, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AdminPanelSettingsIcon
                      sx={{
                        color: companyData.isAllowedByAdmin
                          ? appcolors.primary
                          : "#ff0000",
                        fontSize: "1.5rem",
                      }}
                    />
                  </Box>
                  <Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize="0.75rem"
                      textTransform="uppercase"
                      letterSpacing={0.5}
                    >
                      {t("showInList")}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color={
                        companyData.isAllowedByAdmin
                          ? appcolors.primary
                          : "#ff0000"
                      }
                    >
                      {companyData.isAllowedByAdmin ? t("yes") : t("no")}
                    </Typography>
                  </Stack>
                </Stack>
                <IOSSwitch
                  checked={companyData.isAllowedByAdmin || false}
                  onChange={(e) => handleUpdateAllowedByAdmin(e.target.checked)}
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Basic Information Section */}
        <Typography variant="h6" fontWeight={600} my={2}>
          {t("basicInformation")}
        </Typography>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={6}>
            <Label labelText={t("companyName")} />
            <TextInput
              value={companyData.name || "-"}
              readonly
              onChange={() => {}}
              styleProps={globalStyle.textField}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label labelText={t("email")} />
            <TextInput
              value={companyData.email || "-"}
              readonly
              onChange={() => {}}
              styleProps={globalStyle.textField}
              InputEndIcon={<EmailIcon />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label labelText={t("contactNumber")} />
            <RomanianPhoneInput
              value={companyData.contact || "-"}
              readonly
              onChange={() => {}}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Label labelText={t("cui")} />
            <TextInput
              value={companyData.cui || "-"}
              readonly
              onChange={() => {}}
              styleProps={globalStyle.textField}
              InputEndIcon={<PermIdentityIcon />}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Label labelText={t("createdAt")} />
            <TextInput
              value={
                companyData.createdAt
                  ? moment(companyData.createdAt).format(
                      "MM/DD/YYYY [at] hh:mm A"
                    )
                  : "-"
              }
              readonly
              onChange={() => {}}
              styleProps={globalStyle.textField}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Label labelText={t("updatedAt")} />
            <TextInput
              value={
                companyData.updatedAt
                  ? moment(companyData.updatedAt).format(
                      "MM/DD/YYYY [at] hh:mm A"
                    )
                  : "-"
              }
              readonly
              onChange={() => {}}
              styleProps={globalStyle.textField}
            />
          </Grid>

          <Grid item xs={12}>
            <Label labelText={t("expertise")} />
            <TextInput
              value={companyData.expertise || "-"}
              readonly
              onChange={() => {}}
              multiline
              rows={3}
              styleProps={globalStyle.textField}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Services Section */}
        {companyData.services && companyData.services.length > 0 && (
          <>
            <Typography variant="h6" fontWeight={600} my={2}>
              {t("services")}
            </Typography>
            <Grid container spacing={3} mb={3}>
              {companyData.services.map((serviceItem, index) => {
                const mainService = serviceItem.services;
                const serviceImage = mainService.image;
                const serviceName = mainService.name[currentLanguage];
                const serviceDescription =
                  mainService.description[currentLanguage];

                return (
                  <Grid item xs={12} key={index}>
                    <Box
                      p={3}
                      borderRadius={2}
                      border="1px solid #e0e0e0"
                      backgroundColor="#ffffff"
                      boxShadow="0 2px 8px rgba(0,0,0,0.08)"
                    >
                      {/* Main Service */}
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        mb={3}
                      >
                        {serviceImage && (
                          <Avatar
                            src={serviceImage}
                            sx={{
                              height: { xs: 80, md: 100 },
                              width: { xs: 80, md: 100 },
                              borderRadius: 2,
                              border: "2px solid #e0e0e0",
                            }}
                          />
                        )}
                        <Stack flex={1} spacing={1}>
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            textTransform="capitalize"
                          >
                            {serviceName}
                          </Typography>
                          {serviceDescription && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {serviceDescription}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>

                      {/* Sub Services */}
                      {serviceItem.subServices &&
                        serviceItem.subServices.length > 0 && (
                          <Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              mb={2}
                              sx={{
                                color: appcolors.primary,
                                borderBottom: `2px solid ${appcolors.primary}`,
                                pb: 1,
                                display: "inline-block",
                              }}
                            >
                              {t("subServices")} (
                              {serviceItem.subServices.length})
                            </Typography>
                            <Grid container spacing={2}>
                              {serviceItem.subServices.map(
                                (subService, subIndex) => (
                                  <SubServicesCard
                                    key={subService._id || subIndex}
                                    data={subService}
                                  />
                                )
                              )}
                            </Grid>
                          </Box>
                        )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* Status History Section */}
        {companyData.statusHistory && companyData.statusHistory.length > 0 && (
          <>
            <Typography variant="h6" fontWeight={600} my={2}>
              {t("statusHistory")}
            </Typography>
            <Grid container spacing={2} mb={3}>
              {companyData.statusHistory.map((history, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <Stack spacing={1}>
                      <Chip
                        label={history.status}
                        size="small"
                        sx={{
                          width: "fit-content",
                          backgroundColor:
                            history.status === "unblock"
                              ? "#00c100"
                              : "#ff0000e8",
                          color: "#ffffff",
                          textTransform: "capitalize",
                        }}
                      />
                      <Typography variant="body2">
                        <strong>{t("reason")}:</strong> {history.reason || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t("date")}:</strong>{" "}
                        {moment(history.date).format("MM/DD/YYYY [at] hh:mm A")}
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {companyData.location?.coordinates && (
          <>
            <Typography variant="h6" fontWeight={600} my={2}>
              {t("locationOnMap")}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <LocationMap
                zoom={15}
                address={companyData.location.addressLabel}
                coordinates={{
                  lat: companyData.location.coordinates[1],
                  lng: companyData.location.coordinates[0],
                }}
              />
            </Box>
          </>
        )}
      </PaperContainer>

      <LicenseImageDialog ref={licenseImageDialogRef} />
    </NavigationDrawer>
  );
};

export default CompanyDetailPage;
